import { useState, useMemo, useCallback } from 'react';
import { Template, TemplateField, TemplateChildResource } from 'types';

import ResourceFields from './ResourceFields';

interface ResourcesGroupProps {
    resourceId: string,
    name: string,
    type: TemplateChildResource['type'],
    fields: TemplateField[],
    resources: TemplateChildResource[],
    onChange: (offset: TemplateField['offset'], type: TemplateField['type'], id: string) => void
};

const isDirectChildren = (resource: TemplateChildResource, parentId: string, isBaseResource?: boolean) => {
    const resourceParentId = resource.milestone_id || resource.parent_id;
    if (isBaseResource && !resourceParentId) {
        return true;
    }

    return resourceParentId === parentId;
};

const ResourcesGroup = ({ resourceId, name, type, fields, resources, onChange }: ResourcesGroupProps) => {
    const [collapsed, setCollapsed] = useState(false);

    const hasChildren = useMemo(() => {
        return resources.some(resource => isDirectChildren(resource, resourceId));
    }, [resources, resourceId]);

    const handleToggle = useCallback(() => {
        setCollapsed(prevState => !prevState);
    }, []);

    return (
        <div className="group">
            <ResourceFields
                id={resourceId}
                name={name}
                type={type}
                fields={fields}
                hasChildren={hasChildren}
                collapsed={collapsed}
                onChange={onChange}
                onToggle={hasChildren ? handleToggle: undefined}
            />

            {
                !collapsed && hasChildren ? (
                    <ResourceChildren resourceId={resourceId} isBaseResource={false} resources={resources} onChange={onChange} />
                ) : null
            }
        </div>
    );
}


interface ResourceChildrenProps {
    resourceId: Template['resource_id'],
    isBaseResource?: boolean,
    resources: TemplateChildResource[],
    onChange: (offset: TemplateField['offset'], type: TemplateField['type'], id: string) => void
}

const ResourceChildren = ({ resourceId, isBaseResource = true, resources, onChange }: ResourceChildrenProps) => {
    const directChildren = useMemo(() => {
        return resources.filter(resource => isDirectChildren(resource, resourceId, isBaseResource));
    }, [resources, resourceId, isBaseResource]);

    return (
        <>
            {
                directChildren?.map(resource => (
                    <ResourcesGroup
                        key={resource.id}
                        resourceId={resource.id}
                        name={resource.name}
                        type={resource.type}
                        fields={resource.fields}
                        resources={resources}
                        onChange={onChange}
                    />
                ))
            }
        </>
    )

}

export default ResourceChildren;
